import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { Button, Section } from "@/components/ui";
import { cx } from "@/components/ui/cx";
import {
  childrenOf,
  emailApi,
  type EmailAction,
  type EmailAvailability,
  type EmailItem,
} from "@/lib/mailboxes";

import { useEmailMutation } from "./actions";
import { AliasDialog, DeleteDialog, ForwardDialog, RenameDialog, ResetDialog } from "./dialogs";
import { Freshness, ItemNotice, StatusBadge } from "./parts";

type Simple = "suspend" | "resume" | "recover";
type Dialog =
  | { kind: "rename" }
  | { kind: "reset" }
  | { kind: "delete"; item: EmailItem }
  | { kind: "alias"; alias?: EmailItem }
  | { kind: "forward"; rule?: EmailItem };

/** An action the provider confirms (suspend, resume, recover): one click, a toast when confirmed. */
function ActionButton({
  item,
  action,
  label,
  done,
  onResult,
}: {
  item: EmailItem;
  action: Simple;
  label: string;
  done: string;
  onResult?: (item: EmailItem) => void;
}) {
  const run = useEmailMutation((org, _: void, key) => emailApi.act(org, item.id, action, key), {
    done,
    onResult: (r) => onResult?.(r.item),
  });
  return (
    <CapButton
      item={item}
      action={action}
      label={label}
      pending={run.isPending}
      onClick={() => run.mutate()}
    />
  );
}

/**
 * A button for something the person may or may not do now. When it can't be done it stays in place, focusable, and says
 * why: the server's own sentence for this item (QB-057).
 */
export function CapButton({
  item,
  action,
  label,
  onClick,
  pending = false,
  variant = "secondary",
}: {
  item: EmailItem;
  action: EmailAction;
  label: string;
  onClick: () => void;
  pending?: boolean;
  variant?: "secondary" | "dangerOutline";
}) {
  const cap = item.actions[action];
  const reason = cap?.allowed ? undefined : (cap?.reason ?? "That isn't possible right now.");
  return (
    <Button size="sm" variant={variant} loading={pending} disabledReason={reason} onClick={onClick}>
      {label}
    </Button>
  );
}

function ChildRow({ child, onDialog }: { child: EmailItem; onDialog: (dialog: Dialog) => void }) {
  const alias = child.kind === "alias";
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium break-all text-fg">
            {alias ? child.address : `Forwards to ${child.target}`}
          </span>
          <span className="text-xs text-fg-subtle">{alias ? "Alias" : "Forwarding"}</span>
          <StatusBadge item={child} />
        </div>
        <ItemNotice item={child} />
      </div>
      <div className="flex flex-wrap gap-2">
        {child.state === "suspended" ? (
          <ActionButton
            item={child}
            action="resume"
            label={alias ? "Resume" : "Resume forwarding"}
            done="Resumed"
          />
        ) : (
          <ActionButton
            item={child}
            action="suspend"
            label={alias ? "Pause" : "Pause forwarding"}
            done="Paused"
          />
        )}
        <CapButton
          item={child}
          action="update"
          label={alias ? "Move" : "Change"}
          onClick={() => onDialog(alias ? { kind: "alias", alias: child } : { kind: "forward", rule: child })}
        />
        <CapButton
          item={child}
          action="delete"
          label="Remove"
          variant="dangerOutline"
          onClick={() => onDialog({ kind: "delete", item: child })}
        />
      </div>
    </li>
  );
}

export function MailboxCard({
  mailbox,
  all,
  availability,
  onDeleted,
}: {
  mailbox: EmailItem;
  all: readonly EmailItem[];
  availability: EmailAvailability;
  onDeleted: (item: EmailItem) => void;
}) {
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const kids = childrenOf(all, mailbox.id);
  const aliases = kids.filter((k) => k.kind === "alias");
  const forwards = kids.filter((k) => k.kind === "forward");
  const targets = all.filter(
    (i) =>
      i.kind === "mailbox" && i.fqdn === mailbox.fqdn && (i.state === "active" || i.state === "suspended"),
  );
  const close = () => setDialog(null);
  const canAdd = mailbox.can_manage && (mailbox.state === "active" || mailbox.state === "suspended");
  const addReason = !mailbox.can_manage
    ? "You can view this but not change it. Ask an email manager."
    : !canAdd
      ? "Available once the mailbox is active."
      : undefined;

  return (
    <Section variant="tight" className="border border-border">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold break-all text-fg">{mailbox.address}</h3>
            <StatusBadge item={mailbox} />
          </div>
          {mailbox.display_name && <p className="text-sm text-fg-muted">{mailbox.display_name}</p>}
          <Freshness item={mailbox} />
        </div>
        <div className="flex flex-wrap gap-2">
          <CapButton
            item={mailbox}
            action="update"
            label="Rename"
            onClick={() => setDialog({ kind: "rename" })}
          />
          <ActionButton item={mailbox} action="suspend" label="Suspend" done="Mailbox suspended" />
          <ActionButton item={mailbox} action="resume" label="Resume" done="Mailbox resumed" />
          <CapButton
            item={mailbox}
            action="reset"
            label="Reset password"
            onClick={() => setDialog({ kind: "reset" })}
          />
          <CapButton
            item={mailbox}
            action="delete"
            label="Delete"
            variant="dangerOutline"
            onClick={() => setDialog({ kind: "delete", item: mailbox })}
          />
        </div>
      </div>

      <ItemNotice item={mailbox} />

      <div>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-semibold text-fg hover:underline"
        >
          <ChevronDown className={cx("size-4 transition-transform", !open && "-rotate-90")} aria-hidden />
          Aliases and forwarding
          <span className="font-normal text-fg-subtle">
            ({aliases.length} {aliases.length === 1 ? "alias" : "aliases"}, {forwards.length} forwarding)
          </span>
        </button>
        <div id={panelId} hidden={!open} className="mt-2">
          {kids.length > 0 ? (
            <ul className="divide-y divide-border">
              {kids.map((child) => (
                <ChildRow key={child.id} child={child} onDialog={setDialog} />
              ))}
            </ul>
          ) : (
            <p className="py-2 text-sm text-fg-muted">
              No aliases or forwarding yet. An alias is another address that delivers to this mailbox;
              forwarding sends a copy of its mail to an address elsewhere.
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabledReason={
                addReason ??
                (aliases.length >= availability.alias_limit
                  ? `A mailbox can have up to ${availability.alias_limit} aliases. Remove one to add another.`
                  : undefined)
              }
              onClick={() => setDialog({ kind: "alias" })}
            >
              Add alias
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabledReason={
                addReason ??
                (forwards.length >= availability.forward_limit
                  ? `A mailbox can have up to ${availability.forward_limit} forwarding rules. Remove one to add another.`
                  : undefined)
              }
              onClick={() => setDialog({ kind: "forward" })}
            >
              Forward mail
            </Button>
          </div>
        </div>
      </div>

      {dialog?.kind === "rename" && <RenameDialog item={mailbox} onClose={close} />}
      {dialog?.kind === "reset" && <ResetDialog item={mailbox} onClose={close} />}
      {dialog?.kind === "delete" && (
        <DeleteDialog
          item={dialog.item}
          recoveryDays={availability.recovery_window_days}
          onClose={close}
          onDeleted={dialog.item.kind === "mailbox" ? onDeleted : () => undefined}
        />
      )}
      {dialog?.kind === "alias" && (
        <AliasDialog mailbox={mailbox} alias={dialog.alias} mailboxes={targets} onClose={close} />
      )}
      {dialog?.kind === "forward" && <ForwardDialog mailbox={mailbox} rule={dialog.rule} onClose={close} />}
    </Section>
  );
}

export { ActionButton };
